import dayjs, { type Dayjs } from 'dayjs'

/** Канонический TZ расписания с API (LessonScheduleResource.timezone). */
export const DEFAULT_SCHEDULE_TIMEZONE = 'Europe/Moscow'

function parseInstant(dateStr: string): Date | null {
  const date = new Date(dateStr)
  return Number.isNaN(date.getTime()) ? null : date
}

function formatInTimeZone(date: Date, timeZone: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('ru-RU', { ...options, timeZone }).format(date)
}

/**
 * Форматирует диапазон времени урока без дублирования даты.
 * Один день: "30.06.2026, 11:00 - 12:00"
 * Разные дни: полные даты для start и end.
 */
export function formatLessonTimeRange(
  start?: string | null,
  end?: string | null,
  timeZone = DEFAULT_SCHEDULE_TIMEZONE,
): string {
  if (!start) return '—'

  const startDate = parseInstant(start)
  if (!startDate) return formatDateTimeDMY(start, timeZone)

  if (!end) return formatDateTimeDMY(start, timeZone)

  const endDate = parseInstant(end)
  if (!endDate) return formatDateTimeDMY(start, timeZone)

  const startDay = formatInTimeZone(startDate, timeZone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const endDay = formatInTimeZone(endDate, timeZone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

  if (startDay === endDay) {
    const startFormatted = formatDateTimeDMY(start, timeZone)
    const endTime = formatInTimeZone(endDate, timeZone, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    return `${startFormatted} - ${endTime}`
  }

  return `${formatDateTimeDMY(start, timeZone)} - ${formatDateTimeDMY(end, timeZone)}`
}

/**
 * Форматирует дату в вид DD.MM.YYYY.
 * При некорректной или пустой дате возвращает '—' или исходную строку.
 */
export function formatDateDMY(dateStr?: string | null, timeZone = DEFAULT_SCHEDULE_TIMEZONE): string {
  if (!dateStr) return '—'

  try {
    const date = parseInstant(dateStr)
    if (!date) return dateStr
    return formatInTimeZone(date, timeZone, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

/**
 * Форматирует дату и время в вид DD.MM.YYYY HH:mm в TZ расписания (по умолчанию Europe/Moscow).
 */
export function formatDateTimeDMY(dateStr?: string | null, timeZone = DEFAULT_SCHEDULE_TIMEZONE): string {
  if (!dateStr) return '—'

  try {
    const date = parseInstant(dateStr)
    if (!date) return dateStr
    return formatInTimeZone(date, timeZone, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  } catch {
    return dateStr
  }
}

/**
 * Время урока для компактных виджетов дашборда в TZ расписания.
 * withDate=false → HH:mm; withDate=true → DD.MM HH:mm.
 */
export function formatLessonClock(
  dateStr?: string | null,
  options: { withDate?: boolean } = {},
  timeZone = DEFAULT_SCHEDULE_TIMEZONE,
): string {
  if (!dateStr) return '—'

  try {
    const date = parseInstant(dateStr)
    if (!date) return dateStr
    return formatInTimeZone(date, timeZone, {
      ...(options.withDate
        ? { day: '2-digit' as const, month: '2-digit' as const }
        : {}),
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  } catch {
    return dateStr
  }
}

/** Локальная дата/время (Dayjs или ISO) → ISO UTC для API. */
export function localDateTimeToIso(value: Dayjs | string | Date): string {
  const d = dayjs.isDayjs(value) ? value : dayjs(value)
  return d.toDate().toISOString()
}

/** Строка из `<input type="datetime-local">` (YYYY-MM-DDTHH:mm) → ISO UTC. */
export function dateTimeLocalStringToIso(value: string): string {
  return dayjs(value).toDate().toISOString()
}
